import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUpIcon, HistoryIcon, SquarePenIcon, XIcon } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type JSX,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import {
  sidebarRowActive,
  sidebarRowHover,
  sidebarRowRadius,
} from '@/app/sidebar-primitives.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import {
  resolveCurrentUserRole,
  resolveWorkspaceSharing,
  type WorkspaceSharing,
} from '@/features/workspace/workspace-access.js';
import { useMediaQuery } from '@/hooks/useMediaQuery.js';
import { cn } from '@/lib/utils.js';
import {
  workspaceArtifactQueryOptions,
  workspaceArtifactsQueryOptions,
  restoreWorkspaceArtifact,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';
import {
  knowledgeSourcesQueryOptions,
  useDeleteKnowledgeSource,
} from '@/lib/api/workspace-knowledge.js';
import {
  workspaceMembersQueryOptions,
  workspacesQueryOptions,
} from '@/lib/api/workspaces.js';
import { notify } from '@/lib/notify.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

import { safeDownload } from './artifact-download.js';
import {
  canCurateArtifactKnowledge,
  findArtifactKnowledgeSource,
} from './artifact-knowledge.js';
import { copyArtifactLink } from './artifact-link.js';
import { ArtifactActionsMenu } from './ArtifactActionsMenu.js';
import { ArtifactDeleteDialog } from './ArtifactDeleteDialog.js';
import { ArtifactEditor } from './ArtifactEditor.js';
import { ArtifactFullScreenDialog } from './ArtifactFullScreenDialog.js';
import { ArtifactKnowledgeDialog } from './ArtifactKnowledgeDialog.js';
import { ArtifactContentBody } from './ArtifactPreview.js';
import {
  canAddArtifact,
  canEditArtifact,
  canManageArtifact,
  formatBytes,
  resolvePreviewKind,
  sourceLabel,
  typeLabel,
} from './artifact-display.js';
import { isEditableArtifact } from './artifact-editing.js';
import { ArtifactRenameDialog } from './ArtifactRenameDialog.js';
import { ArtifactShareDialog } from './ArtifactShareDialog.js';
import { FullScreenToggle } from './FullScreenToggle.js';
import { ArtifactUploadRevisionDialog } from './ArtifactUploadRevisionDialog.js';
import { ArtifactVersionHistory } from './ArtifactVersionHistory.js';

/**
 * Width curve of the rail, copied from the left sidebar's collapse in `AppShell` so both sides of the
 * chat move with one motion language.
 */
const PANEL_WIDTH_TRANSITION =
  'width 0.3s cubic-bezier(0.22, 1, 0.36, 1) 0.06s';

/** The drawer's slide, same curve and delay as the rail's width. */
const DRAWER_SLIDE_TRANSITION =
  'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1) 0.06s';

/**
 * How long the closing animation needs before the panel may leave the tree — the width transition plus
 * its delay. Unmounting any earlier would cut the animation off and make the panel vanish.
 */
const CLOSE_ANIMATION_MS = 360;

/**
 * Opacity timing of the panel's contents, mirroring the sidebar's `sidebar-content-in` /
 * `sidebar-content-out`: on close they fade out fast so the rail can shrink against an empty surface;
 * on open they fade in after the rail has already started widening.
 */
function contentFade(isOpen: boolean): string {
  return isOpen ? 'opacity 0.25s ease 0.18s' : 'opacity 0.12s ease';
}

/**
 * The chat's right-hand artifact panel. When the agent registers a file mid-turn, `useChat` opens this
 * on that file so the user sees it without leaving the conversation. It reads the conversation's files
 * from the artifacts registry (not from the turn's stream events), so it still works after a reload:
 * the persisted active id is a durable registry row id.
 *
 * Layout: a resizable column next to the chat on a wide viewport, an overlay drawer below 1024 px (the
 * Teams tab's narrow shell included). Formats the classifier reports as non-previewable — legacy Office
 * files and unknown binaries — stay download-only; {@link ArtifactContentBody} owns that decision.
 *
 * The file's version history is here too, behind a toggle: reading a history is reading, so it is open to
 * anyone who can see the file, and picking an older version previews THAT version's bytes. Restore is
 * gated on the caller's own workspace role ({@link useArtifactViewer}), not on a prop from the shell.
 */
export function ArtifactSidePanel({
  workspaceId,
  conversationId,
  readOnly = false,
}: {
  workspaceId: string | null;
  conversationId: string | null;
  /** Archived chat detail keeps file reads, but exposes no file mutation controls. */
  readOnly?: boolean;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const isOpen = useUiStore((s) => s.artifactPanelOpen);
  const activeArtifactId = useUiStore((s) => s.activeArtifactId);
  const requestedArtifactId = useUiStore((s) => s.requestedArtifactId);
  const width = useUiStore((s) => s.artifactPanelWidth);
  const closePanel = useUiStore((s) => s.closeArtifactPanel);
  const collapsePanel = useUiStore((s) => s.collapseArtifactPanel);
  const setActiveArtifactId = useUiStore((s) => s.setActiveArtifactId);
  const clearRequestedArtifact = useUiStore((s) => s.clearRequestedArtifact);
  const setWidth = useUiStore((s) => s.setArtifactPanelWidth);
  const isWideViewport = useMediaQuery('(min-width: 1024px)');
  /**
   * Keeps the panel in the tree while it animates out. It mounts with the store's open flag (so a
   * reload that restores an open panel doesn't animate in from nothing) and leaves one animation later.
   */
  const [isPresent, setIsPresent] = useState(isOpen);
  /**
   * Whether the panel is in its expanded geometry — what the transitions actually read. Kept apart from
   * `isOpen` because a node that mounts already expanded has no start value to animate FROM: the panel
   * has to paint its collapsed state for one frame first. Initialized from `isOpen` so a panel restored
   * open on load is simply there, with no entrance animation.
   */
  const [isExpanded, setIsExpanded] = useState(isOpen);
  /**
   * Whether the version history is showing. Collapsed by default so opening the panel — which happens on
   * its own, mid-turn — never fetches a history nobody asked for.
   */
  const [historyOpen, setHistoryOpen] = useState(false);
  /** Which version the preview shows. Null is the head, which is what the panel opens on. */
  const [viewingRevision, setViewingRevision] = useState<number | null>(null);
  /** The file a knowledge-base confirmation is open for. */
  const [promoting, setPromoting] = useState<WorkspaceArtifact | null>(null);
  /** The preview becomes an inline text editor only for the current, editable head revision. */
  const [editing, setEditing] = useState(false);
  /**
   * The shown file fills the window in its own dialog, because the panel is too narrow to enlarge in place.
   * The panel drops its own preview meanwhile, so the file is not fetched, parsed and drawn twice.
   */
  const [fullScreen, setFullScreen] = useState(false);
  /** Dialog ownership stays with this surface; the shared menu is deliberately presentation-only. */
  const [uploadingRevision, setUploadingRevision] =
    useState<WorkspaceArtifact | null>(null);
  const [renaming, setRenaming] = useState<WorkspaceArtifact | null>(null);
  const [sharing, setSharing] = useState<WorkspaceArtifact | null>(null);
  const [deleting, setDeleting] = useState<WorkspaceArtifact | null>(null);
  /**
   * A successful edit/upload already returns the new registry row. Keep it on screen while the invalidated
   * chat list refetches, otherwise the panel briefly jumps back to the previous head after saying the save
   * succeeded. Once the query reaches that revision it naturally wins again.
   */
  const [locallyUpdatedArtifact, setLocallyUpdatedArtifact] =
    useState<WorkspaceArtifact | null>(null);
  const viewer = useArtifactViewer(workspaceId, isOpen);
  /**
   * The workspace's knowledge sources — what says whether the shown file is already on the shelf. Read
   * only for a caller who could curate at all, so a viewer's session asks for nothing.
   */
  const shelfQuery = useQuery({
    ...knowledgeSourcesQueryOptions(workspaceId ?? ''),
    enabled: isOpen && workspaceId !== null && canAddArtifact(viewer.role),
  });
  const removeFromShelf = useDeleteKnowledgeSource(workspaceId ?? '');

  const query = useQuery({
    ...workspaceArtifactsQueryOptions(workspaceId ?? '', {
      conversationId: conversationId ?? undefined,
    }),
    enabled: isOpen && workspaceId !== null && conversationId !== null,
  });
  /**
   * Never trust the cache without a conversation. With a null `conversationId` the filter drops out of
   * the query key, which is then the WORKSPACE-WIDE list key the Artifacts section fills — and a
   * disabled query still serves whatever is cached under its key, so every file in the workspace would
   * show up here as "files from this chat".
   */
  const hasChatScope = workspaceId !== null && conversationId !== null;
  const artifacts = hasChatScope ? (query.data ?? []) : [];
  const listedArtifact =
    artifacts.find((entry) => entry.id === activeArtifactId) ?? null;
  /**
   * A file of the workspace that is NOT one of this conversation's — the agent named it in an answer and
   * the reader clicked its chip, so it has to open here like any other file. The list cannot answer for
   * it (it is filtered to the conversation), so it is read by id.
   *
   * Gated on the id having been ASKED FOR ({@link requestedArtifactId}), which is what leaves the
   * ordinary rule untouched: an id that is merely not in this chat's list — a persisted id after a chat
   * switch, a deleted file — is still dead here and still replaced by the chat's newest file. Only asked
   * once the list has settled without it, so the file the panel opens normally costs no extra request.
   * A 404 — deleted, or not shared with this reader — is not retried and leaves the panel saying so,
   * which is the honest answer and NOT a reason to show a different file.
   */
  const hasForeignRequest =
    isOpen &&
    workspaceId !== null &&
    activeArtifactId !== null &&
    activeArtifactId === requestedArtifactId &&
    listedArtifact === null;
  /**
   * When to ASK is a narrower question than whether the file is ours to show: the request waits for the
   * conversation's list to settle, so the file the panel opens the ordinary way costs no extra call.
   */
  const wantsForeignArtifact =
    hasForeignRequest && !query.isLoading && !query.isFetching;
  const foreignQuery = useQuery({
    ...workspaceArtifactQueryOptions(workspaceId ?? '', activeArtifactId ?? ''),
    enabled: wantsForeignArtifact,
  });
  /**
   * The file read by id, and only while the request that fetched it still stands.
   *
   * Gated on {@link hasForeignRequest} rather than on the row alone, because the query key does not
   * change when the reader moves to another conversation and a disabled query still serves whatever is
   * cached under its key: reading it unconditionally kept the previous chat's file on screen after the
   * request had lapsed, and a shown file blocks both the fall-back to this chat's newest file and the
   * collapse, so it stayed there for good.
   *
   * Gated on the REQUEST and not on {@link wantsForeignArtifact}, because that one also waits for the
   * list — and the list refetches mid-turn, every time the agent registers a file. Tying the row to it
   * blinked the cited file out and back for the length of each refetch.
   */
  const foreignArtifact =
    hasForeignRequest && foreignQuery.data?.id === activeArtifactId
      ? foreignQuery.data
      : null;
  const queriedArtifact = listedArtifact ?? foreignArtifact;
  const activeArtifact =
    locallyUpdatedArtifact !== null &&
    queriedArtifact !== null &&
    locallyUpdatedArtifact.id === queriedArtifact.id &&
    locallyUpdatedArtifact.headRevision > queriedArtifact.headRevision
      ? locallyUpdatedArtifact
      : queriedArtifact;
  /**
   * Whether the by-id read has finished having its say. "Not in the list" is not yet "gone": until this
   * is true the panel must not treat the active id as dead, or it would swap onto another file in the
   * window before the answer arrives.
   */
  const foreignSettled =
    !wantsForeignArtifact || foreignQuery.isSuccess || foreignQuery.isError;
  /**
   * The reader asked for a file and it cannot be shown — deleted, or not shared with them. That answer
   * belongs to them: quietly falling back to the chat's newest file would look like the file they clicked
   * and be a different one, which is the mistake this whole change exists to stop.
   */
  const requestFailed = hasForeignRequest && foreignQuery.isError;
  /**
   * Whether the panel is still waiting for something to show. The list arriving is one way; a file read
   * by id is the other, and an empty list is no longer a reason to keep the skeleton up — with a file
   * named in an answer the list stays empty for good and the skeleton would never come down.
   */
  const isAwaitingArtifact =
    activeArtifact === null &&
    (query.isLoading ||
      !foreignSettled ||
      (artifacts.length === 0 && !foreignQuery.isError));
  /** The list comes back newest-first, so the head is the file to fall back to. */
  const newestArtifactId = artifacts[0]?.id ?? null;
  const shownArtifactId = activeArtifact?.id ?? null;
  /** The shown file's shelf row, when it is on the shelf — the row's own id addresses the removal. */
  const shelfRow =
    activeArtifact === null
      ? undefined
      : findArtifactKnowledgeSource(shelfQuery.data ?? [], activeArtifact.id);
  /** The share right the service gates a promotion on, mirrored so nothing renders that would 403. */
  const artifactArchived = activeArtifact?.status === 'archived';
  const mutationsReadOnly = readOnly || artifactArchived;
  const canCurateKnowledge =
    !mutationsReadOnly &&
    activeArtifact !== null &&
    canCurateArtifactKnowledge(activeArtifact, viewer);
  const mayEditArtifact =
    !mutationsReadOnly &&
    activeArtifact !== null &&
    canEditArtifact(activeArtifact, viewer);
  /**
   * Delete or share the shown file. Sharing rides on the same right and NOT on the kind
   * of workspace: the outside half of the share dialog hands out a copy, which a personal workspace can
   * do as well as a shared one — a file received into your own space can be passed on.
   */
  const baseMayManageArtifact =
    activeArtifact !== null && canManageArtifact(activeArtifact, viewer);
  const mayManageArtifact = !mutationsReadOnly && baseMayManageArtifact;
  const mayManageLifecycle = !readOnly && baseMayManageArtifact;
  const mayEditContent =
    mayEditArtifact &&
    activeArtifact !== null &&
    viewingRevision === null &&
    isEditableArtifact(activeArtifact);

  async function restoreShownArtifact(): Promise<void> {
    if (
      workspaceId === null ||
      activeArtifact === null ||
      !artifactArchived ||
      !mayManageLifecycle
    ) {
      return;
    }
    try {
      await restoreWorkspaceArtifact(workspaceId, activeArtifact.id);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['workspace-artifacts', workspaceId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['artifact-access', workspaceId, activeArtifact.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['workspace-knowledge', workspaceId],
        }),
      ]);
      notify.success('File restored');
    } catch (error) {
      notify.error(
        error instanceof Error ? error.message : 'File update failed',
      );
    }
  }

  /** Take the shown file off the knowledge shelf. Only the row goes — the file itself is untouched. */
  function removeShownFromKnowledge(): void {
    if (!shelfRow) return;
    removeFromShelf.mutate(shelfRow.id, {
      onSuccess: () => notify.success('Removed from the knowledge base'),
      onError: (error) => notify.error(error.message),
    });
  }

  /**
   * Another file means another history, so the preview goes back to that file's head. Without this the
   * panel would ask for "version 3" of a file that may only have one.
   */
  useEffect(() => {
    setViewingRevision(null);
    setEditing(false);
  }, [shownArtifactId]);

  /**
   * A file named in an answer belongs to the chat that answer is in. Once the reader MOVES to another
   * conversation the request lapses, and the panel answers for this chat's files again — otherwise a
   * file from the chat left behind would keep the shown place here, and an id that is dead in the new
   * chat would keep blocking the fall-back to its newest file.
   *
   * It fires on a CHANGE and never on arrival, which is the difference between a rule and an accident:
   * the panel is mounted for the whole chat route, so clearing on mount would work only because the
   * click happens to come later — and a remount (leaving the route and coming back, or React's
   * development double-mount) would silently throw away a request the reader had just made. The first
   * conversation this panel sees is therefore adopted, not treated as a move; `null` on arrival, which
   * is what a reload starts with before `/chats` adopts the URL's id, is not a move either.
   */
  const seenConversationId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const previous = seenConversationId.current;
    seenConversationId.current = conversationId;
    if (previous === undefined || previous === null) return;
    if (previous === conversationId) return;
    clearRequestedArtifact();
  }, [conversationId, clearRequestedArtifact]);

  /**
   * Adopt the newest file of this conversation when the active id isn't one of them — the user switched
   * chats, or the file was deleted. Without this the panel would sit on a dead id showing nothing.
   */
  useEffect(() => {
    if (!isOpen || activeArtifact !== null || newestArtifactId === null) return;
    /**
     * A file named in an answer is not in this conversation's list, and it is read by id instead.
     * Adopting before that read answers would replace the file the reader just clicked with the
     * conversation's newest one — the same class of mistake as adopting while the list is behind.
     */
    if (!foreignSettled || requestFailed) return;
    /**
     * Never adopt while the list is behind: a file the agent just created is set active BEFORE the
     * registry list has been refetched, so treating its id as dead would swap the panel onto an older
     * file precisely in the auto-open flow this feature exists for. `isStale` covers the window between
     * the invalidation and the refetch starting; `isFetching` covers the refetch itself.
     */
    if (query.isStale || query.isFetching) return;
    setActiveArtifactId(newestArtifactId);
  }, [
    isOpen,
    activeArtifact,
    foreignSettled,
    requestFailed,
    newestArtifactId,
    setActiveArtifactId,
    query.isStale,
    query.isFetching,
  ]);

  /**
   * A settled empty list leaves nothing to show, so drop the open flag rather than letting the store
   * claim an open panel while the toggle stays hidden behind that same flag. Collapse, not close: this
   * is not a user dismissal, and spending the close counter here would suppress the auto-open for a
   * file that arrives later in the turn.
   */
  useEffect(() => {
    if (!isOpen) return;
    /**
     * A missing conversation is NOT "nothing to show" — on a reload `activeConversationId` starts null
     * and `/chats` adopts the URL's id in an effect after the first render, so collapsing here would
     * throw away a panel the user left open (the persistence this panel promises). Only a settled,
     * genuinely empty list for a REAL conversation collapses it.
     */
    if (!hasChatScope) return;
    if (query.isLoading || query.isFetching || artifacts.length > 0) return;
    /**
     * A conversation of its own may hold no files at all and still have something to show: a file the
     * answer named. Collapsing on the empty LIST would shut the panel on the file the reader just asked
     * for, so the by-id read has to have failed too before there is genuinely nothing here.
     */
    if (!foreignSettled || foreignArtifact !== null || requestFailed) return;
    collapsePanel();
  }, [
    isOpen,
    hasChatScope,
    query.isLoading,
    query.isFetching,
    artifacts.length,
    foreignSettled,
    foreignArtifact,
    requestFailed,
    collapsePanel,
  ]);

  /** Mount on open; on close collapse at once and hold the panel for one animation, then drop it. */
  useEffect(() => {
    if (isOpen) {
      setIsPresent(true);
      return;
    }
    setIsExpanded(false);
    const timer = window.setTimeout(
      () => setIsPresent(false),
      CLOSE_ANIMATION_MS,
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [isOpen]);

  /**
   * Expand one frame after the collapsed state has been painted — this effect runs post-paint, so a
   * single frame is enough for the browser to have a value to transition from. Without it the panel
   * mounted at its full width and simply popped into place.
   */
  useEffect(() => {
    if (!isPresent || !isOpen || isExpanded) return;
    const frame = window.requestAnimationFrame(() => setIsExpanded(true));
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [isPresent, isOpen, isExpanded]);

  /**
   * Esc closes the panel. Skipped while a modal dialog is open — that Esc belongs to the dialog, and
   * closing the panel underneath it would lose the user's place for no reason they asked for. Radix
   * listens in the capture phase and can already have closed its dialog by the time this listener runs,
   * so the open-dialog query alone misses it; the `preventDefault` Radix calls on a handled Esc is the
   * reliable signal.
   */
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      closePanel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closePanel]);

  if (!isPresent) return null;
  /**
   * An empty list only means "no files" once it has SETTLED. The panel and the toolbar toggle share one
   * query, so a chat that had no files leaves `[]` in the cache — and TanStack Query keeps `isLoading`
   * false while cached data refetches. Bailing here would blank the panel for the whole refetch that
   * follows the first-file auto-open, with the toggle hidden behind the open flag: nothing on screen.
   *
   * A file named in an answer is the one thing an empty list does not settle: it belongs to the
   * workspace rather than to this chat, so the list is empty for good and the panel would never render
   * the very file the reader clicked. `hasForeignRequest` also stays true once that read has failed,
   * which is what lets the panel say so instead of vanishing.
   */
  if (
    artifacts.length === 0 &&
    !query.isLoading &&
    !query.isFetching &&
    !hasForeignRequest
  )
    return null;

  /**
   * Live width drag. The handle sits on the panel's LEFT edge, so a pointer moving left widens it —
   * hence the inverted delta. The store clamps.
   *
   * The pointer is CAPTURED to the handle, and the listeners live on the handle rather than the window.
   * That is what survives dragging across the PDF preview: pointer events over a cross-document iframe
   * are delivered to the iframe's own document, so a window-level `pointerup` never arrives and the drag
   * stays stuck to the cursor until the next click lands outside the frame. Capture retargets every event
   * for this pointer to the handle, so a drag over the frame — or off the 4-px handle — still ends.
   */
  const startResize = (event: ReactPointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const handle = event.currentTarget;
    const { pointerId } = event;
    /**
     * Capture is what survives the PDF frame, but it is not guaranteed: an environment without the API
     * (jsdom) or one that throws would otherwise leave a drag with no listeners on the path the pointer
     * actually takes. So the target is chosen by whether capture is genuinely held, and the window
     * fallback is the pre-capture behaviour — good everywhere except over a cross-document frame.
     */
    let captured = false;
    try {
      handle.setPointerCapture?.(pointerId);
      captured = handle.hasPointerCapture?.(pointerId) ?? false;
    } catch {
      captured = false;
    }
    const dragTarget: EventTarget = captured ? handle : window;
    const startX = event.clientX;
    const startWidth = width;
    const onMove = (moveEvent: Event): void => {
      const { clientX } = moveEvent as globalThis.PointerEvent;
      setWidth(startWidth + (startX - clientX));
    };
    const onEnd = (): void => {
      dragTarget.removeEventListener('pointermove', onMove);
      dragTarget.removeEventListener('pointerup', onEnd);
      dragTarget.removeEventListener('pointercancel', onEnd);
      if (captured) {
        handle.removeEventListener('lostpointercapture', onEnd);
        if (handle.hasPointerCapture?.(pointerId))
          handle.releasePointerCapture?.(pointerId);
      }
    };
    dragTarget.addEventListener('pointermove', onMove);
    /**
     * Every way a drag can end must clean up, or a surviving move listener keeps writing the persisted
     * width: the button comes up, the OS or browser cancels the pointer, or — only when capture is held —
     * capture is lost some other way, such as the node being removed mid-drag.
     */
    dragTarget.addEventListener('pointerup', onEnd);
    dragTarget.addEventListener('pointercancel', onEnd);
    if (captured) handle.addEventListener('lostpointercapture', onEnd);
  };

  const panel = (
    <aside
      aria-label="Files from this chat"
      data-mode={isWideViewport ? 'column' : 'drawer'}
      data-state={isExpanded ? 'open' : 'closed'}
      className={panelClassName(isWideViewport)}
      style={{
        ...(isWideViewport
          ? {
              width,
              opacity: isExpanded ? 1 : 0,
              transition: contentFade(isExpanded),
            }
          : {
              transform: isExpanded ? 'translateX(0)' : 'translateX(100%)',
              transition: DRAWER_SLIDE_TRANSITION,
            }),
      }}
    >
      <ArtifactPanelHeader
        artifact={activeArtifact}
        revision={viewingRevision ?? undefined}
        onFullScreen={
          !editing &&
          activeArtifact !== null &&
          resolvePreviewKind(
            activeArtifact.contentType,
            activeArtifact.name,
          ) !== 'none'
            ? () => setFullScreen(true)
            : undefined
        }
        onEditContent={
          !editing && mayEditContent
            ? () => {
                setHistoryOpen(false);
                setEditing(true);
              }
            : undefined
        }
        onUploadNewVersion={
          !editing && mayEditArtifact && activeArtifact !== null
            ? () => setUploadingRevision(activeArtifact)
            : undefined
        }
        onVersionHistory={
          !editing && activeArtifact !== null
            ? () => {
                setEditing(false);
                setHistoryOpen((open) => !open);
              }
            : undefined
        }
        onRename={
          !editing && mayEditArtifact && activeArtifact !== null
            ? () => setRenaming(activeArtifact)
            : undefined
        }
        onCopyLink={
          activeArtifact !== null && !artifactArchived
            ? () => void copyArtifactLink(activeArtifact, viewer)
            : undefined
        }
        onDownload={
          activeArtifact !== null
            ? () => safeDownload(activeArtifact, viewingRevision ?? undefined)
            : undefined
        }
        onShare={
          !editing && mayManageArtifact && activeArtifact !== null
            ? () => setSharing(activeArtifact)
            : undefined
        }
        knowledgeAction={
          !editing && canCurateKnowledge
            ? {
                mode: shelfRow === undefined ? 'add' : 'remove',
                onSelect:
                  shelfRow === undefined
                    ? () => setPromoting(activeArtifact)
                    : removeShownFromKnowledge,
              }
            : undefined
        }
        onDelete={
          !editing && mayManageLifecycle && activeArtifact !== null
            ? () => setDeleting(activeArtifact)
            : undefined
        }
        onRestoreFile={
          !editing && artifactArchived && mayManageLifecycle
            ? () => void restoreShownArtifact()
            : undefined
        }
        onClose={closePanel}
      />
      {activeArtifact &&
        workspaceId !== null &&
        mayEditArtifact &&
        !editing && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-sidebar-border px-3 py-2">
            {mayEditContent && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setHistoryOpen(false);
                  setEditing(true);
                }}
              >
                <SquarePenIcon className="size-4" /> Edit content
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUploadingRevision(activeArtifact)}
            >
              <FileUpIcon className="size-4" /> Upload new version
            </Button>
          </div>
        )}
      {activeArtifact && viewingRevision !== null && (
        <div className="flex items-center gap-2 border-b border-sidebar-border px-4 py-2">
          <p role="status" className="text-xs text-sidebar-section">
            {`Showing version ${viewingRevision}. The latest is version ${activeArtifact.headRevision}.`}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => setViewingRevision(null)}
          >
            Back to latest
          </Button>
        </div>
      )}
      {isAwaitingArtifact ? (
        <Skeleton className="m-4 h-72 rounded-lg" />
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          {activeArtifact && workspaceId !== null ? (
            editing ? (
              <div className="h-full overflow-y-auto overscroll-contain">
                <ArtifactEditor
                  workspaceId={workspaceId}
                  artifact={activeArtifact}
                  onSaved={(updated) => {
                    setLocallyUpdatedArtifact(updated);
                    setEditing(false);
                  }}
                  onCancel={() => setEditing(false)}
                />
              </div>
            ) : fullScreen ? (
              <p className="py-10 text-center text-sm text-sidebar-section">
                Showing full screen.
              </p>
            ) : (
              <ArtifactContentBody
                workspaceId={workspaceId}
                artifact={activeArtifact}
                revision={viewingRevision ?? undefined}
                fill
              />
            )
          ) : (
            <p className="py-10 text-center text-sm text-sidebar-section">
              {requestFailed
                ? 'This file is not available. It may have been deleted, or it is not shared with you.'
                : 'Select a file to preview it.'}
            </p>
          )}
        </div>
      )}
      {activeArtifact && workspaceId !== null && !editing && (
        <div className="shrink-0 border-t border-sidebar-border px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((open) => !open)}
          >
            <HistoryIcon className="size-4" />
            {`Version history · ${activeArtifact.headRevision}`}
          </Button>
          {historyOpen && (
            <div className="mt-2">
              <ArtifactVersionHistory
                workspaceId={workspaceId}
                artifact={activeArtifact}
                canRestore={mayEditArtifact}
                selectedRevision={viewingRevision}
                onSelectRevision={setViewingRevision}
              />
            </div>
          )}
        </div>
      )}
      {artifacts.length > 1 && !editing && (
        <ArtifactSwitcher
          artifacts={artifacts}
          activeArtifactId={activeArtifact?.id ?? null}
          onSelect={setActiveArtifactId}
        />
      )}
      {promoting && workspaceId !== null && (
        <ArtifactKnowledgeDialog
          workspaceId={workspaceId}
          files={[promoting]}
          sharing={viewer.sharing}
          onClose={() => setPromoting(null)}
        />
      )}
      {uploadingRevision && workspaceId !== null && (
        <ArtifactUploadRevisionDialog
          workspaceId={workspaceId}
          artifact={uploadingRevision}
          onSaved={setLocallyUpdatedArtifact}
          onClose={() => setUploadingRevision(null)}
        />
      )}
      {renaming && workspaceId !== null && (
        <ArtifactRenameDialog
          workspaceId={workspaceId}
          artifact={renaming}
          onClose={() => setRenaming(null)}
        />
      )}
      {sharing && workspaceId !== null && (
        <ArtifactShareDialog
          workspaceId={workspaceId}
          artifact={sharing}
          sharing={viewer.sharing}
          open
          onOpenChange={(open) => !open && setSharing(null)}
        />
      )}
      {fullScreen && activeArtifact && workspaceId !== null && (
        <ArtifactFullScreenDialog
          workspaceId={workspaceId}
          artifact={activeArtifact}
          revision={viewingRevision ?? undefined}
          onClose={() => setFullScreen(false)}
        />
      )}
      {deleting && workspaceId !== null && (
        <ArtifactDeleteDialog
          workspaceId={workspaceId}
          artifact={deleting}
          onDeleted={closePanel}
          onClose={() => setDeleting(null)}
        />
      )}
    </aside>
  );

  /**
   * Wide viewport: the rail animates its WIDTH (the left sidebar's curve and delay) while the panel
   * itself keeps its full width, pinned to the rail's right edge and clipped — so it is revealed from
   * the screen edge instead of having its contents re-wrap on every animation frame. The resize handle
   * lives on the un-clipped rail so it isn't cut off, and only while the panel is open.
   */
  if (isWideViewport) {
    return (
      <div
        className="relative z-10 h-full shrink-0"
        style={{
          width: isExpanded ? width : 0,
          transition: PANEL_WIDTH_TRANSITION,
        }}
      >
        <div className="absolute inset-0 overflow-hidden">{panel}</div>
        {isExpanded && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize file panel"
            onPointerDown={startResize}
            className="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize hover:bg-sidebar-accent"
          />
        )}
      </div>
    );
  }
  return (
    <>
      <div
        aria-hidden
        onClick={closePanel}
        className={cn(
          'fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm',
          /** Transparent during the entrance delay and the exit fade — it must not swallow a chat
           *  click while it is invisible. */
          !isExpanded && 'pointer-events-none',
        )}
        style={{
          opacity: isExpanded ? 1 : 0,
          transition: contentFade(isExpanded),
        }}
      />
      {panel}
    </>
  );
}

/**
 * The caller as the artifact access predicates read them — their identity id and their role in this
 * workspace — resolved from the workspace registry and the member directory the chat route already keeps
 * cached. That is what lets the panel gate Restore honestly without a role prop drilled down from the
 * shell into a component that AppShell mounts.
 *
 * No platform-admin elevation, exactly like the `/artifacts` route: not one of the service's artifact
 * predicates accepts an admin flag, so elevating here would only render a Restore the service answers 403
 * to. `enabled` keeps both reads idle while the panel is closed.
 *
 * `sharing` comes from the same registry row and decides the promotion confirmation's wording, through the
 * same fail-safe rule the `/artifacts` route uses: an unresolved workspace is `unknown`, and the confirmation
 * warns until it is known personal. It is handed to the share dialog for the same reason, which is what
 * makes that dialog drop its visibility half where there is nobody to open the file to. It does not gate
 * `Share…` itself — a copy can leave any workspace.
 */
function useArtifactViewer(
  workspaceId: string | null,
  enabled: boolean,
): {
  userId: string | null;
  role: string | null;
  sharing: WorkspaceSharing;
} {
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const currentUserId = identity.identity?.userId ?? null;
  const workspacesQuery = useQuery({ ...workspacesQueryOptions(), enabled });
  const membersQuery = useQuery({
    ...workspaceMembersQueryOptions(workspaceId ?? ''),
    enabled: enabled && workspaceId !== null,
  });
  const workspace = workspacesQuery.data?.find(
    (candidate) => candidate.id === workspaceId,
  );
  return {
    userId: currentUserId,
    role: resolveCurrentUserRole({
      workspace,
      members: membersQuery.data,
      currentUserId,
      isAdmin: false,
    }),
    sharing: resolveWorkspaceSharing(workspace),
  };
}

/**
 * Column next to the chat on a wide viewport; overlay drawer pinned to the right edge below that.
 * Wears the left sidebar's surface — `bg-sidebar` on `border-sidebar-border` with
 * `text-sidebar-foreground` — so the two rails read as one chrome around the chat rather than as a
 * panel of a different material.
 */
function panelClassName(isWideViewport: boolean): string {
  return cn(
    'flex min-h-0 flex-col overflow-hidden border-sidebar-border bg-sidebar text-sidebar-foreground',
    isWideViewport
      ? 'absolute inset-y-0 right-0 border-l'
      : 'fixed inset-y-0 right-0 z-50 w-full max-w-sm border-l shadow-2xl',
  );
}

/**
 * File name, registry metadata, the canonical file menu and close action.
 *
 * Copying the link belongs here for the same reason the panel exists: the user is reading the file in the
 * middle of a conversation, which is exactly where they decide to pass it on. What it copies is the
 * Artifacts section pointed at this file, so the recipient opens it under their own membership.
 *
 * There is deliberately **no attach action here.** This panel is scoped to the conversation on screen —
 * its query passes that `conversationId` and it renders nothing without one — so every file it lists is
 * already a file of this chat, and attaching one to the chat it came from adds nothing. The two entries
 * that do attach a file reach a chat the file is not in yet: the composer's `+` picker and "Add to chat"
 * in the Artifacts section.
 *
 * The knowledge-base entry is NOT the same case, and the reasoning above does not carry over to it: adding
 * a file to the knowledge base is an action on the WORKSPACE, not on this conversation, so a file already
 * in this chat has just as much to gain from it here as it would in the Artifacts section. It is offered
 * only to a caller holding the share right, and it flips to taking the file off the shelf once it is on it.
 */
function ArtifactPanelHeader({
  artifact,
  revision,
  onFullScreen,
  onEditContent,
  onUploadNewVersion,
  onVersionHistory,
  onRename,
  onCopyLink,
  onDownload,
  onShare,
  knowledgeAction,
  onDelete,
  onRestoreFile,
  onClose,
}: {
  artifact: WorkspaceArtifact | null;
  /**
   * The version on screen, when it is not the head. Download follows it: handing the user the newest
   * bytes while they are reading an older version would give them a file they are not looking at.
   */
  revision?: number;
  /** Opens the file full screen — absent when the file has nothing to preview. */
  onFullScreen?: () => void;
  onEditContent?: () => void;
  onUploadNewVersion?: () => void;
  onVersionHistory?: () => void;
  onRename?: () => void;
  onCopyLink?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  knowledgeAction?: {
    mode: 'add' | 'remove';
    onSelect: () => void;
  };
  onDelete?: () => void;
  onRestoreFile?: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="flex items-start gap-2 border-b border-sidebar-border px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-sidebar-foreground">
          {artifact?.name ?? 'Files from this chat'}
        </p>
        {artifact && (
          <p className="mt-0.5 truncate text-xs text-sidebar-section">
            {`${sourceLabel(artifact.source)} · ${typeLabel(artifact.contentType, artifact.name)} · ${formatBytes(artifact.size)}`}
          </p>
        )}
      </div>
      {artifact && onFullScreen && (
        <FullScreenToggle
          maximized={false}
          onToggle={onFullScreen}
          shortcutHint={false}
        />
      )}
      {artifact && (
        <ArtifactActionsMenu
          artifactName={artifact.name}
          onEditContent={onEditContent}
          onUploadNewVersion={onUploadNewVersion}
          onVersionHistory={onVersionHistory}
          onRename={onRename}
          onCopyLink={onCopyLink}
          onDownload={onDownload ?? (() => safeDownload(artifact, revision))}
          onShare={onShare}
          knowledgeAction={knowledgeAction}
          onDelete={onDelete}
          onRestoreFile={onRestoreFile}
        />
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Close file panel"
        onClick={onClose}
      >
        <XIcon className="size-4" />
      </Button>
    </div>
  );
}

/** The conversation's other files, so the user can switch between them without leaving the chat. */
function ArtifactSwitcher({
  artifacts,
  activeArtifactId,
  onSelect,
}: {
  artifacts: WorkspaceArtifact[];
  activeArtifactId: string | null;
  onSelect: (artifactId: string) => void;
}): JSX.Element {
  return (
    <div className="shrink-0 border-t border-sidebar-border px-3 py-2.5">
      <p className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-sidebar-section">
        Files in this chat
      </p>
      <ul className="max-h-40 space-y-0.5 overflow-y-auto pr-1">
        {artifacts.map((artifact) => {
          const isActive = artifact.id === activeArtifactId;
          return (
            <li key={artifact.id}>
              <button
                type="button"
                onClick={() => onSelect(artifact.id)}
                aria-current={isActive ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs transition-colors',
                  sidebarRowRadius,
                  sidebarRowHover,
                  isActive && cn(sidebarRowActive, 'font-medium'),
                )}
              >
                <span className="truncate">{artifact.name}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
